import React, { useEffect, useState, useCallback } from 'react';
import storeApi from '../../../../../services/storeApi';
import CurrencyInput from 'react-currency-input-field';
import { Radio, Select } from 'antd';
import { TWButton, TWModal } from '../../../../../components/ui';
import { OrderTypes } from '../../../../../data/enums';
import { PlatformTypes } from '../../../../../data/enums';
import { TrashIcon } from '@heroicons/react/20/solid';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const ExtraFeesUpsertModal = ({
  selectedExtraFee,
  setSelectedExtraFee,
  show,
  companyDetail,
  onCloseUpsert,
  onClickDelete,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [storeList, setStoreList] = useState([]);
  const [errors] = useState({
    name: false,
    storeid: false,
  });

  const getStoreList = useCallback(async () => {
    setLoading(true);

    try {
      const query = `companyid=${companyDetail.id}`;
      const list = await storeApi.list(query);
      if (list.data.data.length > 0) {
        setStoreList(list.data.data);
      }
    } catch (error) {}
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getStoreList();
  }, [getStoreList]);

  const onFormChange = (value, key) => {
    setSelectedExtraFee((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Percentage fees are a percent (0-100) of the order subtotal; the api
  // rejects values outside that range, so block them here too.
  const percentOutOfRange =
    Boolean(selectedExtraFee.israte) &&
    selectedExtraFee.price !== '' &&
    selectedExtraFee.price != null &&
    (Number(selectedExtraFee.price) < 0 ||
      Number(selectedExtraFee.price) > 100);

  return (
    <TWModal
      open={show}
      onClose={onCloseUpsert}
      size="2xl"
      panelClassName="dark:text-slate-200"
    >
      <div className="space-y-5">
                <div className="col-span-2 sm:col-span-1 w-full">
                  <div className="">
                    <label
                      htmlFor="image"
                      className="block pb-1  text-sm font-medium text-gray-700 dark:text-slate-200"
                    >
                      Store*
                    </label>
                    <Select
                      size="large"
                      style={{
                        width: '100%',
                      }}
                      placeholder="Store"
                      onChange={(e) => {
                        onFormChange(e, 'storeid');
                      }}
                      options={storeList.map((a) => ({
                        label: a.name,
                        value: a.id,
                      }))}
                      value={selectedExtraFee.storeid}
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                  >
                    Name*
                  </label>
                  <div className="mt-1">
                    <input
                      value={selectedExtraFee.name}
                      onChange={(e) => onFormChange(e.target.value, 'name')}
                      type="text"
                      name="rolename"
                      id="rolename"
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                      placeholder="Name"
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.name && (
                        <p className="text-red-700">Name can not be empty</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="ordertypes"
                    className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                  >
                    Order Types*
                  </label>
                  <div className="mt-1">
                    <Select
                      mode="multiple"
                      allowClear
                      size="large"
                      style={{
                        width: '100%',
                      }}
                      placeholder="Please select order types"
                      defaultValue={selectedExtraFee?.ordertypes?.map((x) => x)}
                      onChange={(val) => onFormChange(val, 'ordertypes')}
                      options={Object.entries(OrderTypes).map(([id, name]) => ({
                        label: name,
                        value: Number(id),
                      }))}
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.stores && (
                        <p className="text-red-700">
                          Order Types can not be empty
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="platforms"
                    className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                  >
                    Platforms*
                  </label>
                  <div className="mt-1">
                    <Select
                      mode="multiple"
                      allowClear
                      size="large"
                      style={{
                        width: '100%',
                      }}
                      placeholder="Please select platforms"
                      defaultValue={selectedExtraFee?.platforms?.map((x) => x)}
                      onChange={(val) => onFormChange(val, 'platforms')}
                      options={Object.entries(PlatformTypes).map(
                        ([id, name]) => ({
                          label: name,
                          value: Number(id),
                        })
                      )}
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.stores && (
                        <p className="text-red-700">
                          Platforms can not be empty
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="feetype"
                    className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                  >
                    Fee type*
                  </label>
                  <div className="mt-1">
                    {/* Controlled (not defaultValue): reopening the modal for a
                        different fee must never show a stale mode. Switching
                        modes clears the value — a "2.50" typed as dollars must
                        not silently become 2.5%. */}
                    <Radio.Group
                      id="feetype"
                      optionType="button"
                      buttonStyle="solid"
                      size="large"
                      value={selectedExtraFee.israte ? 'rate' : 'fixed'}
                      onChange={(e) => {
                        setSelectedExtraFee((prev) => ({
                          ...prev,
                          israte: e.target.value === 'rate',
                          price: '',
                        }));
                      }}
                      options={[
                        { label: 'Fixed amount ($)', value: 'fixed' },
                        { label: 'Percentage (%)', value: 'rate' },
                      ]}
                    />
                  </div>
                </div>
                <div className="col-span-4">
                  <label
                    htmlFor="price"
                    className="block text-sm font-medium leading-6 text-gray-900 dark:text-slate-400"
                  >
                    {selectedExtraFee.israte ? 'Percentage*' : 'Price*'}
                  </label>
                  <div className="relative mt-2 rounded-md shadow-sm">
                    <CurrencyInput
                      id="input-price"
                      name="price"
                      placeholder={selectedExtraFee.israte ? '0' : '0.00'}
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                      prefix={selectedExtraFee.israte ? '' : '$'}
                      suffix={selectedExtraFee.israte ? '%' : ''}
                      value={selectedExtraFee.price}
                      decimalSeparator="."
                      groupSeparator=","
                      decimalScale={2}
                      step={1}
                      decimalsLimit={2}
                      onValueChange={(value, name, values) =>
                        onFormChange(value, 'price')
                      }
                    />
                  </div>
                  <div className="mt-1 min-h-[1rem] text-xs">
                    {selectedExtraFee.israte && percentOutOfRange && (
                      <p className="text-red-700">
                        Percentage must be between 0 and 100.
                      </p>
                    )}
                    {selectedExtraFee.israte &&
                      !percentOutOfRange &&
                      Number(selectedExtraFee.price) > 0 && (
                        <p className="text-slate-500 dark:text-slate-400">
                          {Number(selectedExtraFee.price)}% of the order
                          subtotal — $
                          {((50 * Number(selectedExtraFee.price)) / 100).toFixed(
                            2,
                          )}{' '}
                          on a $50.00 order.
                        </p>
                      )}
                  </div>
                  {errors.price && (
                    <div className="h-2 mt-1  text-xs">
                      <p className="text-red-700">Price can not be empty</p>
                    </div>
                  )}
                </div>
                <div className="h-2 mt-1  text-xs"></div>
                <div className="mt-5 sm:mt-6 grid z-10 sm:grid-flow-row-dense grid-cols-12 gap-3 py-4 sm:py-0">
                  {selectedExtraFee.id && (
                    <TWButton
                      variant="danger"
                      icon={TrashIcon}
                      disabled={loading}
                      loading={loading}
                      onClick={() => onClickDelete(selectedExtraFee)}
                      className="col-span-2 w-full"
                    />
                  )}
                  <TWButton
                    variant="default"
                    label="Cancel"
                    disabled={loading}
                    onClick={() => onCloseUpsert()}
                    className={classNames(
                      selectedExtraFee.id ? 'col-span-5' : 'col-span-6',
                      'w-full',
                    )}
                  />
                  <TWButton
                    variant="primary"
                    label={
                      selectedExtraFee.id ? 'Edit Extra Fee' : 'Add Extra Fee'
                    }
                    disabled={
                      loading ||
                      percentOutOfRange ||
                      selectedExtraFee.price === '' ||
                      selectedExtraFee.price == null
                    }
                    loading={loading}
                    onClick={() => onSubmit(selectedExtraFee)}
                    className={classNames(
                      selectedExtraFee.id ? 'col-span-5' : 'col-span-6',
                      'w-full',
                    )}
                  />
                </div>
      </div>
    </TWModal>
  );
};

export default ExtraFeesUpsertModal;
